import React, { useState, useCallback } from 'react';
import { cx } from '#packages/util';
import { connectWithScope } from '#packages/apilib';
import { withRestrictions } from '#packages/editorRestrictions';
import { mapStateToProps, mapDispatchToProps } from './gapBoxMapper';
import { ResizeGapsScope } from '../resizeGapsScope';
import styles from './gapBox.scss';

import GapHeightInput from './gapHeightInput';

import type { GapBoxStateProps, GapBoxDispatchProps } from './gapBoxMapper';

interface GapBoxProps extends GapBoxStateProps, GapBoxDispatchProps {}

const GapBox: React.FC<GapBoxProps> = ({
  currentHeight,
  setGapHeight,
  positionCSS,
  isResizing,
  isVisible,
  startDrag,
  gap,
}) => {
  const [isHeightInputVisible, setIsHeightInputVisible] = useState(false);

  const onMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      const { pageX: initialPageX, pageY: initialPageY } = e;
      const onMouseUp = (e: MouseEvent) => {
        if (e.pageX === initialPageX && e.pageY === initialPageY) {
          setIsHeightInputVisible(true);
        }
        window.removeEventListener('mouseup', onMouseUp);
      };
      window.addEventListener('mouseup', onMouseUp);
      startDrag(e, gap);
    },
    [gap, startDrag],
  );

  if (!isVisible) {
    return null;
  }

  return (
    <div
      style={positionCSS}
      className={cx(styles.gapBox, {
        [styles.isResizing]: isResizing,
      })}
    >
      {isHeightInputVisible && (
        <div className={styles.heightInput}>
          <GapHeightInput
            currentHeight={currentHeight}
            onBlur={() => setIsHeightInputVisible(false)}
            onEscapePressed={() => setIsHeightInputVisible(false)}
            onEnterPressed={(newHeight) => {
              setGapHeight(newHeight, gap);
              setIsHeightInputVisible(false);
            }}
          />
        </div>
      )}
      <div className={styles.resizeHandle} onMouseDown={onMouseDown} />
      {isResizing && (
        <div className={styles.heightIndication}>
          <span>{currentHeight}</span>
        </div>
      )}
    </div>
  );
};

export default withRestrictions('rEditor_hover-gap-box')(
  connectWithScope(
    () => ResizeGapsScope,
    GapBox,
    mapStateToProps,
    mapDispatchToProps,
    {
      rerenderOnMouseOps: true,
    },
  ),
);
